import { Button, Modal } from "antd";
import { clsx } from "clsx";
import { AiOutlineClose } from "react-icons/ai";

import type { FeedbackStep, FeedbackWidgetProps } from "./types";

import { CommentField } from "./CommentField";
import s from "./FeedbackWidget.module.css";
import { useFeedbackWidget } from "./hooks/useFeedbackWidget";
import { RatingRow } from "./RatingRow";
import { SentimentSelector } from "./SentimentSelector";
import { SuccessToast } from "./SuccessToast";

export const FeedbackWidget = ({ config, onSubmit }: FeedbackWidgetProps) => {
  const {
    isOpen,
    isSubmitting,
    sentiment,
    ratings,
    commentText,
    error,
    toastVisible,
    visibleStepIndexes,
    allStepsComplete,
    open,
    close,
    selectSentiment,
    setRating,
    setComment,
    handleSubmit,
    completeToast,
    dismissToast,
  } = useFeedbackWidget({ config, onSubmit });

  const renderStep = (step: FeedbackStep) => {
    switch (step.type) {
      case "sentiment":
        return (
          <SentimentSelector
            question={step.question}
            options={step.options}
            value={sentiment}
            onChange={selectSentiment}
            disabled={isSubmitting}
          />
        );
      case "rating":
        return (
          <div className={s.ratingBlock}>
            <p className={s.prompt}>{step.prompt}</p>
            <div className={s.ratingRows}>
              {step.rows.map(row => (
                <RatingRow
                  key={row.key}
                  label={row.label}
                  value={ratings[row.key] ?? 0}
                  onChange={value => setRating(row.key, value)}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>
        );
      case "comment": {
        const label =
          step.labelsByOption?.[sentiment ?? ""] ?? step.label ?? "";
        return (
          <CommentField
            label={label}
            placeholder={step.placeholder}
            value={commentText}
            onChange={setComment}
            optionalSuffix={step.optionalSuffix}
            maxLength={step.maxLength}
            disabled={isSubmitting}
          />
        );
      }
    }
  };

  const header = (
    <header className={s.header}>
      <span aria-hidden="true" className={s.iconTile}>
        {config.modal.headerIcon}
      </span>
      <h2 className={s.title}>{config.modal.title}</h2>
      <Button
        type="text"
        aria-label={config.modal.closeButtonAriaLabel}
        data-testid="feedback-close"
        className={s.close}
        disabled={isSubmitting}
        onClick={close}
        icon={<AiOutlineClose aria-hidden="true" />}
      />
    </header>
  );

  const footer = (
    <footer className={s.footer}>
      {error && (
        <p role="alert" className={s.error} data-testid="feedback-error">
          {error}
        </p>
      )}
      <Button
        type="primary"
        data-testid="feedback-submit"
        className={clsx(s.submit, !allStepsComplete && s.submitDisabled)}
        disabled={!allStepsComplete}
        loading={isSubmitting}
        onClick={handleSubmit}
      >
        {config.submit.label}
      </Button>
    </footer>
  );

  return (
    <>
      <Button
        type="primary"
        data-testid="feedback-launcher"
        className={s.launcher}
        aria-expanded={isOpen}
        aria-label={config.launcher.ariaLabel}
        icon={<span className={s.launcherIcon}>{config.launcher.icon}</span>}
        tabIndex={isOpen ? -1 : 0}
        onClick={open}
      >
        {config.launcher.label}
      </Button>

      <Modal
        open={isOpen}
        onCancel={close}
        afterClose={completeToast}
        destroyOnHidden
        closable={false}
        footer={footer}
        title={header}
        className={s.modal}
        transitionName=""
        maskTransitionName=""
      >
        <section className={s.steps}>
          {visibleStepIndexes.map(index => (
            <div key={`step-${index}`}>{renderStep(config.steps[index])}</div>
          ))}
        </section>
      </Modal>

      {toastVisible && (
        <SuccessToast
          message={config.toast.message}
          durationMs={config.toast.durationMs}
          onDismiss={dismissToast}
        />
      )}
    </>
  );
};
